import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
const Hero = () => {
  return <section className="relative pt-20 overflow-hidden bg-gradient-to-br from-blue-100 via-blue-50 to-white" data-id="element-343">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden" data-id="element-344">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-blue-200 rounded-full opacity-20 blur-3xl" data-id="element-345"></div>
        <div className="absolute top-40 -left-20 w-80 h-80 bg-green-200 rounded-full opacity-20 blur-3xl" data-id="element-346"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-yellow-200 rounded-full opacity-20 blur-3xl" data-id="element-347"></div>
        <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-pink-200 rounded-full opacity-20 blur-3xl" data-id="element-348"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 relative z-10" data-id="element-349">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-350">
          <div className="lg:col-span-6 lg:pr-8 order-2 md:order-1" data-id="element-351">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-navy-950 leading-tight mb-6" data-id="element-352">
              <span className="block mb-2" data-id="element-353">Elevate Your</span>
              <span className="block text-blue-500 relative" data-id="element-354">
                Workplace Drinks
                <svg className="absolute -bottom-3 left-0 w-full h-3 text-blue-200" viewBox="0 0 200 8" preserveAspectRatio="none" aria-hidden="true" data-id="element-355">
                  <path d="M0,5 C50,0 150,0 200,5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" data-id="element-356"></path>
                </svg>
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-lg" data-id="element-357">
              Delight your team with sustainable, delicious, and cost-effective
              beverage solutions that boost workplace satisfaction.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-358">
              <div className="flex items-center bg-white/80 backdrop-blur-sm p-3 rounded-lg shadow-sm transform hover:scale-105 transition duration-300" data-id="element-359">
                <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-360" />
                <span className="text-gray-700" data-id="element-361">
                  29 Delicious Clean-Label Flavors
                </span>
              </div>
              <div className="flex items-center bg-white/80 backdrop-blur-sm p-3 rounded-lg shadow-sm transform hover:scale-105 transition duration-300" data-id="element-362">
                <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-363" />
                <span className="text-gray-700" data-id="element-364">
                  90% Plastic Waste Reduction
                </span>
              </div>
              <div className="flex items-center bg-white/80 backdrop-blur-sm p-3 rounded-lg shadow-sm transform hover:scale-105 transition duration-300" data-id="element-365">
                <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-366" />
                <span className="text-gray-700" data-id="element-367">Up to 50% Cost Savings</span>
              </div>
              <div className="flex items-center bg-white/80 backdrop-blur-sm p-3 rounded-lg shadow-sm transform hover:scale-105 transition duration-300" data-id="element-368">
                <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-369" />
                <span className="text-gray-700" data-id="element-370">AI + IoT Smart Monitoring</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-371">
              <a href="#book-demo" className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-lg shadow-lg text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all transform hover:scale-105 hover:shadow-xl" aria-label="Book a free SmartSoda demo for your workplace" data-id="element-372">
                Book a Free Demo
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-373" />
              </a>
              <a href="#book-demo" className="inline-flex items-center justify-center px-8 py-4 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all transform hover:scale-105 hover:shadow-md" aria-label="Get a SmartSoda quote in 24 hours for your workplace" data-id="element-374">
                Get a Quote in 24h
              </a>
            </div>
          </div>
          <div className="lg:col-span-6 relative order-1 md:order-2" data-id="element-375">
            <div className="relative" data-id="element-376">
              {/* Main image with colorful drinks */}
              <div className="bg-white p-3 rounded-3xl shadow-2xl relative z-10 transform hover:scale-[1.02] transition-transform duration-500 overflow-hidden" data-id="element-377">
                <div className="rounded-2xl overflow-hidden" data-id="element-378">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/hHhEoMy85SDiV4PdKgvjZh/HeroSmartSoda.webp" alt="SmartSoda beverage machine with variety of colorful fruit-infused drinks for workplace refreshment" className="w-full object-cover" width="600" height="400" fetchpriority="high" loading="eager" decoding="async" data-id="element-379" />
                </div>
              </div>
              <div className="absolute -bottom-4 left-10 bg-gradient-to-r from-yellow-200 to-amber-200 border-2 border-yellow-400 text-yellow-800 px-6 py-4 rounded-full shadow-xl z-20 transform rotate-3 hover:rotate-0 transition-transform duration-300 animate-pulse hover:animate-none" data-id="element-380">
                <span className="font-bold text-base flex items-center" data-id="element-381">
                  <span className="mr-1" data-id="element-382">🍹</span> Happy hour – every hour{' '}
                  <span className="ml-1" data-id="element-383">✨</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Add animation keyframes */}
      <style jsx data-id="element-384">{`
        @keyframes progress {
          0% {
            width: 0%;
          }
          100% {
            width: 90%;
          }
        }
      `}</style>
    </section>;
};
export default Hero;